import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configuración del worker de PDF.js
//pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdfjs-dist/build/pdf.worker.min.mjs';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const PDFCardViewerCarouselZoomFocus = ({ pdfUrl, pdfTitulo }) => {
  // Refs para el contenedor (fijo y con scroll) y el canvas
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const currentRenderTaskRef = useRef(null);


  // Refs para controlar el arrastre (panning y/o swipe para cambiar página)
  const draggingRef = useRef(false);
  const pointerDownTimeRef = useRef(0);
  const pointerDownStartRef = useRef({ x: 0, y: 0 });
  const lastPointerPosRef = useRef({ x: 0, y: 0 });

  // Estados del PDF
  const [pdf, setPdf] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  // Escala base calculada según el ancho del contenedor (para renderizar sin zoom)
  const [baseScale, setBaseScale] = useState(1);
  // Estado para controlar el "zoom" mediante el ancho del contenedor interno;
  // valor inicial: '100%', luego '250%', '400%' y vuelve a '100%'
  const [stylew, setStylew] = useState('100%');

  // Cargar el PDF al montar el componente
  useEffect(() => {
    const loadingTask = pdfjsLib.getDocument(pdfUrl);
    loadingTask.promise
      .then((loadedPdf) => {
        setPdf(loadedPdf);
        setTotalPages(loadedPdf.numPages);
        renderPage(loadedPdf, pageNumber, baseScale, stylew);
      })
      .catch(console.error);
  }, [pdfUrl]);

  // Función para renderizar la página usando PDF.js
  // Se calcula el "zoom" efectivo multiplicando la baseScale por el factor derivado de stylew
  const renderPage = (pdfDoc, pageNum, baseScale, stylew) => {
    pdfDoc.getPage(pageNum).then((page) => {
      const zoomFactor = parseInt(stylew) / 100; // Ejemplo: '250%' → 2.5
      const effectiveScale = baseScale * zoomFactor;
      const viewport = page.getViewport({ scale: effectiveScale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Cancela la renderización anterior si existe
      if (currentRenderTaskRef.current) {
        currentRenderTaskRef.current.cancel();
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      context.clearRect(0, 0, canvas.width, canvas.height);

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      // Inicia la renderización y guarda la tarea
      const renderTask = page.render(renderContext);
      currentRenderTaskRef.current = renderTask;

      // Cuando la renderización termine, limpia la referencia
      renderTask.promise.then(() => {
        currentRenderTaskRef.current = null;
      }).catch((error) => {
        if (error.name === 'RenderingCancelledException') {
          // La tarea fue cancelada; no es un error real
        } else {
          console.error('Error during render:', error);
        }
      });

    });
  };

  // Re-renderiza la página cada vez que cambie el PDF, la página, la baseScale o el zoom (stylew)
  useEffect(() => {
    if (pdf) {
      renderPage(pdf, pageNumber, baseScale, stylew);
    }
  }, [pdf, pageNumber, baseScale, stylew]);

  // Ajustar la escala base según el tamaño del contenedor usando ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pdf) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const containerWidth = entry.contentRect.width;
        pdf.getPage(1).then((page) => {
          const unscaledViewport = page.getViewport({ scale: 1 });
          const newBaseScale = containerWidth / unscaledViewport.width;
          setBaseScale(newBaseScale);
        });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [pdf]);

  // --- EVENTOS DE ZOOM (doble clic / doble toque) ---
  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;

    // Obtener el zoom actual (antes de actualizarlo)
    const currentZoom = parseInt(stylew) / 100;
    const rect = container.getBoundingClientRect();
    // Se calcula la posición del clic relativa al contenedor (incluyendo scroll)
    const clickX = e.clientX - rect.left + container.scrollLeft;
    const clickY = e.clientY - rect.top + container.scrollTop;
    // Convertir a "coordenadas base" (sin zoom)
    const baseClickX = clickX / currentZoom;
    const baseClickY = clickY / currentZoom;

    // Definir el nuevo nivel de zoom en función del valor actual de stylew
    let newStylew;
    if (stylew === '100%') {
      newStylew = '200%';
    } else if (stylew === '200%') {
      newStylew = '300%';
    } else {
      newStylew = '100%';
    }
    setStylew(newStylew);

    // Luego de actualizar el zoom, reajustar el scroll para centrar el punto base
    setTimeout(() => {
      const newZoom = parseInt(newStylew) / 100;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      container.scrollLeft = baseClickX * newZoom - containerWidth / 2;
      container.scrollTop = baseClickY * newZoom - containerHeight / 2;
    }, 100);
  };

  // --- EVENTOS DE ARRASTRE (panning y/o cambio de página) ---
  // Para distinguir entre un barrido rápido (para cambiar página) y un arrastre sostenido (panning)
  const handleMouseDown = (e) => {
    pointerDownTimeRef.current = Date.now();
    pointerDownStartRef.current = { x: e.clientX, y: e.clientY };
    lastPointerPosRef.current = { x: e.clientX, y: e.clientY };
    draggingRef.current = true;
  };

  const handleMouseMove = (e) => {
    if (!draggingRef.current) return;
    // Si hay zoom (stylew !== '100%'), se permite el panning
    if (stylew !== '100%') {
      const container = containerRef.current;
      const deltaX = e.clientX - lastPointerPosRef.current.x;
      const deltaY = e.clientY - lastPointerPosRef.current.y;
      container.scrollLeft -= deltaX;
      container.scrollTop -= deltaY;
      lastPointerPosRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseUp = (e) => {
    draggingRef.current = false;
    const duration = Date.now() - pointerDownTimeRef.current;
    const container = containerRef.current;
    if (!container) return;
    const containerWidth = container.clientWidth;
    const startX = pointerDownStartRef.current.x;
    const endX = e.clientX;
    const deltaX = endX - startX;
    // Sin zoom, se aplica la lógica clásica de swipe
    if (stylew === '100%') {
      if (duration < 300 && Math.abs(deltaX) > 50) {
        deltaX > 0 ? goToPreviousPage() : goToNextPage();
      }
    } else {
      // Con zoom, solo se cambia de página si el gesto rápido se inicia en una esquina
      const rect = container.getBoundingClientRect();
      const initialX = pointerDownStartRef.current.x - rect.left;
      if (duration < 300 && Math.abs(deltaX) > 50) {
        if (initialX < containerWidth * 0.1 && deltaX > 0) {
          goToPreviousPage();
        } else if (initialX > containerWidth * 0.9 && deltaX < 0) {
          goToNextPage();
        }
      }
    }
  };

  const handleMouseLeave = () => {
    draggingRef.current = false;
  };

  // Handlers para eventos táctiles (muy similares a los de mouse)
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    pointerDownTimeRef.current = Date.now();
    pointerDownStartRef.current = { x: touch.clientX, y: touch.clientY };
    lastPointerPosRef.current = { x: touch.clientX, y: touch.clientY };
    draggingRef.current = true;
  };

  const handleTouchMove = (e) => {
    if (!draggingRef.current) return;
    if (stylew !== '100%') {
      const touch = e.touches[0];
      const container = containerRef.current;
      const deltaX = touch.clientX - lastPointerPosRef.current.x;
      const deltaY = touch.clientY - lastPointerPosRef.current.y;
      container.scrollLeft -= deltaX;
      container.scrollTop -= deltaY;
      lastPointerPosRef.current = { x: touch.clientX, y: touch.clientY };
    }
  };

  const handleTouchEnd = (e) => {
    draggingRef.current = false;
    const duration = Date.now() - pointerDownTimeRef.current;
    const container = containerRef.current;
    if (!container) return;
    const containerWidth = container.clientWidth;
    const startX = pointerDownStartRef.current.x;
    const endX = e.changedTouches[0].clientX;
    const deltaX = endX - startX;
    if (stylew === '100%') {
      if (duration < 300 && Math.abs(deltaX) > 50) {
        deltaX > 0 ? goToPreviousPage() : goToNextPage();
      }
    } else {
      const rect = container.getBoundingClientRect();
      const initialX = pointerDownStartRef.current.x - rect.left;
      if (duration < 300 && Math.abs(deltaX) > 50) {
        if (initialX < containerWidth * 0.1 && deltaX > 0) {
          goToPreviousPage();
        } else if (initialX > containerWidth * 0.9 && deltaX < 0) {
          goToNextPage();
        }
      }
    }
  };

  // Funciones para cambiar de página
  const goToPreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
      if (containerRef.current) {
        containerRef.current.scrollLeft = 0;
        containerRef.current.scrollTop = 0;
      }
    }
  };

  const goToNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber((prev) => prev + 1);
      if (containerRef.current) {
        containerRef.current.scrollLeft = 0;
        containerRef.current.scrollTop = 0;
      }
    }
  };

  return (
    <div
        style={{
            //width: 'calc(100vw - 40px)', // Resta 40px para dejar 20px a cada lado
            //maxWidth: '600px',           // En pantallas grandes, no se expande más allá de 600px
            //maxHeight: 'calc(50vh - 20px)',
            maxHeight: '50vh',
            margin: '0px auto',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: '10px',
        }}
        >
      {/* Encabezado del Card */}
      <div style={{  background: '#f5f5f5', textAlign: 'center' }}>
        <strong>{pdfTitulo}</strong>
      </div>

      {/* Contenedor del canvas: tamaño fijo y con overflow para panning */}
      <div
        ref={containerRef}
        style={{
          //width: '100%',
          //height: '50vh',
          background: '#fff',
          overflow: 'auto',
          position: 'relative',
          userSelect: 'none'
        }}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Contenedor interno cuyo ancho se controla con stylew */}
        <div style={{ width: stylew }}>
          <canvas ref={canvasRef} style={{ display: 'block' }} />
        </div>
      </div>

      {/* Controles de navegación de página */}
      <div style={{ padding: '10px', textAlign: 'center' }}>
         <button className='pdf-button' onClick={goToPreviousPage} disabled={pageNumber <= 1}>
          Anterior
        </button>
        <span style={{ margin: '0 10px' }}>
          Página {pageNumber} de {totalPages}
        </span>
        <button className='pdf-button' onClick={goToNextPage} disabled={pageNumber >= totalPages}>
          Siguiente
        </button> 
      </div>


    </div>
  );
};

export default PDFCardViewerCarouselZoomFocus;
